import { ProjectToStationResponse as MeasurementResponse } from '@data-access/geospatial/alignments';
import {
  PickEventDetail,
  PickingTool,
  Vector3,
  Web3DEventName,
  Web3DViewer,
} from '@technology/web3d';
import { AlignmentMeasurementTool } from '@technology/web3d-plugin-markup';
import { partialPointToVector3 } from '@trimble/utilities';
import {
  isAlignmentMeasurementInteractionAllowedForWrapper,
  Web3DViewerWrapper,
} from '@trimble/web3d-utils';
import {
  MeasurementsCapabilityProvider,
  SnapType,
  Web3dTool,
} from '@trimble/web3d-providers';
import {
  Accessor,
  Setter,
  createEffect,
  createRoot,
  createSignal,
} from 'solid-js';
import { CapabilityCategory } from '../base/models/capability-category';
import { SimpleMeasurementCapabilityBase } from './base-simple-measurement-capability';

type GetAlignmentMeasurementFn =
  | ((
      point: Vector3,
      viewer: Web3DViewer,
      useCant?: boolean,
      useGradient?: boolean
    ) => Promise<MeasurementResponse | null>)
  | null;

export class AlignmentMeasurementCapability extends SimpleMeasurementCapabilityBase {
  protected measurementType: Web3dTool = Web3dTool.Picking;
  public override category: CapabilityCategory = CapabilityCategory.Markup;
  public readonly setUseGradient: Setter<boolean>;
  public readonly setUseCant: Setter<boolean>;
  public readonly useGradient: Accessor<boolean>;
  public readonly useCant: Accessor<boolean>;
  private readonly pickEventHandler: (
    e: CustomEvent,
    viewer: Web3DViewer
  ) => void;
  private readonly alignmentMeasurementFn: GetAlignmentMeasurementFn | null;
  // Remembers snap types broadened during setup so teardown/deactivate can restore them.
  private readonly savedSnapTypes = new WeakMap<Web3DViewer, number[]>();

  constructor(
    protected override readonly measurementsProvider: MeasurementsCapabilityProvider,
    alignmentMeasurementFn: GetAlignmentMeasurementFn | null
  ) {
    super(measurementsProvider);

    const [useCant, setUseCant] = createSignal<boolean>(false);
    const [useGradient, setUseGradient] = createSignal<boolean>(false);
    this.useCant = useCant;
    this.setUseCant = setUseCant;
    this.useGradient = useGradient;
    this.setUseGradient = setUseGradient;

    this.alignmentMeasurementFn = alignmentMeasurementFn;

    this.pickEventHandler = this.handlePickEvent;
  }

  private hasAnyViewerAlignmentInteractionAllowed(): boolean {
    return this.viewerWrappers().some((w) =>
      isAlignmentMeasurementInteractionAllowedForWrapper(w)
    );
  }

  private teardownAlignmentMeasurementForViewer(viewer: Web3DViewer): void {
    this.removeAllViewerEventListeners(viewer);
    this.releaseMeasurementTool(viewer);
    if (viewer.activeTool === this.measurementType) {
      viewer.tools.get(PickingTool)?.cancel();
    }
    this.restoreSnapTypes(viewer);
  }

  public override trackIsEnabled(): void {
    createEffect(() => {
      if (this.alignmentMeasurementFn == null) {
        void this.disable();
        return;
      }
      const wrappers = this.viewerWrappers();
      for (const w of wrappers) {
        void w.isAlignmentMeasurementInteractionAllowed?.();
      }
      if (!this.hasAnyViewerAlignmentInteractionAllowed()) {
        void this.disable();
        return;
      }
      this.enable();
    });
  }

  public override startMeasurement(): Promise<void> {
    if (
      this.alignmentMeasurementFn == null ||
      !this.hasAnyViewerAlignmentInteractionAllowed()
    ) {
      return Promise.resolve();
    }
    this.effectDispose?.();
    this.effectDispose = createRoot((dispose) => {
      createEffect(() => {
        const wrappers = this.viewerWrappers();
        for (const w of wrappers) {
          void w.isAlignmentMeasurementInteractionAllowed?.();
        }

        const allowedWrappers = wrappers.filter((w) =>
          isAlignmentMeasurementInteractionAllowedForWrapper(w)
        );

        if (allowedWrappers.length === 0) {
          for (const w of wrappers) {
            this.teardownAlignmentMeasurementForViewer(w.viewer);
          }
          queueMicrotask(() => {
            const wrappersNow = this.viewerWrappers();
            const allowedNow = wrappersNow.filter((w) =>
              isAlignmentMeasurementInteractionAllowedForWrapper(w)
            );
            if (allowedNow.length === 0 && this.isActive()) {
              void this.stopMeasurement();
            }
          });
          return;
        }

        this.activate();
        void this.setupWeb3dMeasurementTool(allowedWrappers);

        for (const w of wrappers) {
          if (!isAlignmentMeasurementInteractionAllowedForWrapper(w)) {
            this.teardownAlignmentMeasurementForViewer(w.viewer);
          }
        }
      });
      return dispose;
    });
    return Promise.resolve();
  }

  private handlePickEvent(e: CustomEvent, viewer: Web3DViewer): void {
    const measurementTool: AlignmentMeasurementTool = viewer.tools.get(
      AlignmentMeasurementTool
    )!;

    const detail = e.detail as PickEventDetail;
    // PickingTool dispatches `pick` twice per click (once for the tap, once for
    // the multi-click observable); ignore the multi-click and hover dispatches
    // to avoid creating duplicate measurements.
    if (detail.hover || detail.multiClick != null) return;

    const worldPosition = detail.worldPosition;
    if (!worldPosition) return;

    const fn = this.alignmentMeasurementFn;
    if (fn != null) {
      fn(worldPosition, viewer, this.useCant(), this.useGradient())
        .then((result) => {
          if (result == null) {
            console.warn('Alignment measurement result is null!');
            return;
          }

          if (
            result.groundCoordinate == null ||
            result.pointCoordinate == null ||
            result.alignmentCoordinate == null
          ) {
            return;
          }
          const ground = partialPointToVector3(result.groundCoordinate);
          const point = partialPointToVector3(result.pointCoordinate);
          const alignment = partialPointToVector3(result.alignmentCoordinate);
          measurementTool.add(
            {
              snapType: SnapType.POINT as number,
              point: ground,
            },
            {
              snapType: SnapType.POINT as number,
              point: point,
            },
            {
              snapType: SnapType.POINT as number,
              point: alignment,
            },
            result.alignmentStation?.station
          );
        })
        .catch((error: unknown) => {
          console.error('Error getting alignment measurement:', error);
        });
    }
  }

  protected override async setupWeb3dMeasurementTool(
    viewerWrappers: Web3DViewerWrapper[]
  ): Promise<void> {
    if (this.alignmentMeasurementFn == null) {
      throw new Error('Alignment measurement function is not configured');
    }
    viewerWrappers.forEach((viewerWrapper) => {
      const viewer = viewerWrapper.viewer;
      this.removeAllViewerEventListeners(viewer);
      // Setting activeTool triggers PickingTool.enabled = true, which subscribes
      // the shared Cursor3D for us.
      viewer.activeTool = viewer.tools.get(PickingTool)!.name;
      this.gateMeasurementTool(viewer);
      // Broaden snap types so PickingTool can produce an intersection on any
      // geometry hit, not just POINT. Restored by teardown/deactivate.
      if (!this.savedSnapTypes.has(viewer)) {
        this.savedSnapTypes.set(viewer, [...viewer.settings.snapTypes]);
      }
      viewer.settings.snapTypes = [
        SnapType.FACE as number,
        SnapType.LINE as number,
        SnapType.POINT as number,
        SnapType.CENTER_LINE as number,
        SnapType.CENTER_POINT as number,
        SnapType.CONTROL_POINT as number,
        SnapType.MIDPOINT as number,
      ];
      this.addViewerEventListener(
        viewer,
        'pick' as Web3DEventName.Pick,
        (e: Event): void => {
          this.pickEventHandler(e as CustomEvent, viewer);
        }
      );
    });
    return Promise.resolve();
  }

  public async useGradientMeasurement(): Promise<void> {
    this.setUseGradient(!this.useGradient());
    return Promise.resolve();
  }

  public async useCantMeasurement(): Promise<void> {
    this.setUseCant(!this.useCant());
    return Promise.resolve();
  }

  public override deactivate(): boolean {
    for (const viewer of [...this.viewerEventListeners.keys()]) {
      this.teardownAlignmentMeasurementForViewer(viewer);
    }
    this.clearAllViewerEventListeners();
    return super.deactivate();
  }

  public override cleanup(): Promise<void> {
    void this.stopMeasurement();
    return super.cleanup();
  }

  private restoreSnapTypes(viewer: Web3DViewer): void {
    const saved = this.savedSnapTypes.get(viewer);
    if (saved !== undefined) {
      viewer.settings.snapTypes = saved;
      this.savedSnapTypes.delete(viewer);
    }
  }
}
